import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

export const QUOTE_STATUS_BADGE = {
  draft: "bg-slate-200 text-slate-700 hover:bg-slate-200",
  sent: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  accepted: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  rejected: "bg-red-100 text-red-800 hover:bg-red-100",
  expired: "bg-amber-100 text-amber-900 hover:bg-amber-100",
  cancelled: "bg-slate-100 text-slate-500 hover:bg-slate-100",
  converted: "bg-violet-100 text-violet-800 hover:bg-violet-100",
};

const STATUS_OPTIONS = ["", "draft", "sent", "accepted", "rejected", "expired", "cancelled", "converted"];

export default function Quotes() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const language = i18n.language?.split("-")[0] || "en";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter) params.set("status", statusFilter);
      params.set("page_size", "100");
      const { data } = await api.get(`/quotes?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, t("quotes.toasts.error")));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, t]);

  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  return (
    <div className="space-y-8" data-testid="quotes-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("quotes.title")}
          </h1>
          <p className="text-sm text-slate-500">
            {t("quotes.subtitle", { count: total })}
          </p>
        </div>
        <Button
          onClick={() => navigate("/app/quotes/new")}
          data-testid="quotes-new-btn"
          className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          <Plus className="h-4 w-4" />
          {t("quotes.new")}
        </Button>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("quotes.searchPlaceholder")}
            data-testid="quotes-search"
            className="ps-9"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="quotes-status-filter">
            <SelectValue placeholder={t("quotes.statusAll")} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s || "all"} value={s || "all"}>
                {s ? t(`quotes.status.${s}`) : t("quotes.statusAll")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("quotes.table.number")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("quotes.table.customer")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("quotes.table.issued")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("quotes.table.expires")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("quotes.table.status")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("quotes.table.total")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
                  {t("quotes.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((q) => (
                <TableRow
                  key={q.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/app/quotes/${q.id}`)}
                  data-testid={`quote-row-${q.quote_number}`}
                >
                  <TableCell data-label={t("quotes.table.number")}>
                    <Link
                      to={`/app/quotes/${q.id}`}
                      className="font-mono text-sm font-medium text-slate-900 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {q.quote_number}
                    </Link>
                  </TableCell>
                  <TableCell className="text-slate-700" data-label={t("quotes.table.customer")}>
                    {q.customer?.name}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm" data-label={t("quotes.table.issued")}>
                    {formatDate(q.issue_date, language)}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm" data-label={t("quotes.table.expires")}>
                    {formatDate(q.expires_at, language)}
                  </TableCell>
                  <TableCell data-label={t("quotes.table.status")}>
                    <Badge
                      className={QUOTE_STATUS_BADGE[q.status] || QUOTE_STATUS_BADGE.draft}
                      data-testid={`quote-status-${q.quote_number}`}
                    >
                      {t(`quotes.status.${q.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="text-end font-medium tabular-nums text-slate-900"
                    data-label={t("quotes.table.total")}
                  >
                    {formatCurrency(q.total, q.currency, language)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
